'use client'

import Link from 'next/link'

import { IconChevRight } from '@/app/components/atoms/Icons'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA, NOT_SET } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import { cx } from '@/app/lib/utils'

import { DEFAULT_ARIA_LABEL, SEPARATOR_ICON_SIZE } from './Breadcrumb.constants'
import styles from './Breadcrumb.module.scss'
import type { BreadcrumbItem, BreadcrumbProps } from './Breadcrumb.types'

/**
 * Breadcrumb [atom] — DS Entradas.
 *
 * Ruta de navegación jerárquica. Separador: chevron-right. El último nivel es el
 * actual y se marca con `aria-current="page"`.
 *
 * Client Component: cada nivel enlazado dispara `click` (`click_type: 'breadcrumb'`)
 * al hacer clic, siguiendo el mismo patrón que `PageIntroBreadcrumb`. Tolerante a la
 * ausencia de `PageAnalyticsProvider` (ver [[analytics-shared-components]]).
 *
 * Accesibilidad (WCAG 2.1 AA — 1.3.1, 2.4.8):
 * - Envuelto en `<nav aria-label>` para identificar la región.
 * - Lista ordenada `<ol>` que refleja la jerarquía.
 * - El nivel actual se renderiza como `<span aria-current="page">` (no enlace).
 * - Los niveles padre con `href` son enlaces; sin `href`, texto plano.
 * - Los separadores son decorativos (`aria-hidden`).
 */
export function Breadcrumb({
  items,
  ariaLabel = DEFAULT_ARIA_LABEL,
  className,
  style,
  dark = false,
  channel = false,
}: BreadcrumbProps) {
  const analytics = useOptionalPageAnalytics()

  if (items.length === 0) return null

  const lastIndex = items.length - 1

  function handleClick(item: BreadcrumbItem, index: number) {
    analytics?.trackClick({
      click_type: 'breadcrumb',
      click_subtype: NO_APLICA,
      link_text: slugify(item.label),
      link_url: item.href ?? NO_APLICA,
      link_domain: NO_APLICA,
      module_name: NOT_SET,
      module_type: NO_APLICA,
      nav_level: String(index),
      nav_path: items
        .slice(0, index + 1)
        .map((crumb) => slugify(crumb.label))
        .join('/'),
      action_detail: NO_APLICA,
      action_value: NO_APLICA,
      action_context: NO_APLICA,
    })
  }

  return (
    <nav
      aria-label={ariaLabel}
      className={cx(styles.nav, dark && styles.navDark, channel && styles.navChannel, className)}
      style={style}
    >
      <ol className={styles.list}>
        {items.map((item, index) => {
          const isCurrent = index === lastIndex
          const showSeparator = !isCurrent

          return (
            <li key={`${item.label}-${index}`} className={styles.item}>
              {isCurrent ? (
                <span className={styles.current} aria-current="page">
                  {item.label}
                </span>
              ) : item.href ? (
                <Link
                  href={item.href}
                  className={styles.link}
                  onClick={() => handleClick(item, index)}
                >
                  {item.label}
                </Link>
              ) : (
                <span className={styles.text}>{item.label}</span>
              )}

              {showSeparator && (
                <span className={styles.separator} aria-hidden="true">
                  <IconChevRight size={SEPARATOR_ICON_SIZE} />
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
